import { useState } from "react";
import { msg, t } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  CheckCircle2,
  CloudUpload,
  LogOut,
  Pencil,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "@tanstack/react-router";
import { deleteOwnAccount, type AuthUser } from "@/lib/auth";
import { userApi } from "@/lib/api/services/user.api";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { FileUploader } from "@/components/common/FileUploader";
import {
  APP_TIMEZONE_OPTIONS,
  normalizeAppTimezone,
} from "@/lib/config/timezones";
import { cn } from "@/lib/utils";
import { ExportActivityLogDialog } from "./ExportActivityLogDialog";
import {
  Field,
  FieldGrid,
  FormSection,
  SectionDivider,
  SettingRow,
  TabHeader,
} from "./profile-form";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
];

const COUNTRY_OPTIONS = [
  { value: "United States", label: msg`United States` },
  { value: "Canada", label: msg`Canada` },
  { value: "United Kingdom", label: msg`United Kingdom` },
  { value: "Brazil", label: msg`Brazil` },
  { value: "Germany", label: msg`Germany` },
];

const PHONE_CODES = ["+1", "+44", "+55", "+49", "+33"];

const controlClass =
  "h-11 rounded-lg border-(--color-border) bg-(--color-surface-content)";

const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, { error: () => t`First name is required` }),
  lastName: z
    .string()
    .min(1, { error: () => t`Last name is required` }),
  birthdate: z.string().optional(),
  phoneCode: z.string(),
  phone: z.string().optional(),
  timezone: z.string(),
  language: z.string(),
  addressLine1: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  country: z.string(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

/** `DD.MM.YYYY` (or `/`,`-`) → UTC ISO date, or undefined if unparseable/empty. */
function dmyToIso(value?: string): string | undefined {
  const match = (value ?? "").trim().match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (!match) return undefined;
  const [, dd, mm, yyyy] = match;
  const date = new Date(Date.UTC(Number(yyyy), Number(mm) - 1, Number(dd)));
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
}

/** ISO date → `DD.MM.YYYY` for the birthdate input. */
function isoToDmy(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const dd = String(date.getUTCDate()).padStart(2, "0");
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${date.getUTCFullYear()}`;
}

function splitPhone(full?: string): { phoneCode: string; phone: string } {
  const value = (full ?? "").trim();
  const code = PHONE_CODES.find((c) => value.startsWith(c));
  if (code) {
    return { phoneCode: code, phone: value.slice(code.length).trim() };
  }
  return { phoneCode: "+1", phone: value };
}

function getInitials(user: AuthUser): string {
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return (
    name
      .split(" ")
      .filter(Boolean)
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "U"
  );
}

export function GeneralDetailsTab({ user }: { user: AuthUser }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { signOut, isSigningOut } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);

  const defaultValues: ProfileFormValues = {
    firstName: user.firstName ?? "",
    lastName: user.lastName ?? "",
    birthdate: isoToDmy(user.birthDate),
    timezone: normalizeAppTimezone(user.timezone),
    language: user.preferredLanguage ?? "en",
    addressLine1: user.addressLine1 ?? "",
    city: user.city ?? "",
    state: user.state ?? "",
    zip: user.postalCode ?? "",
    country: user.country ?? "United States",
    ...splitPhone(user.phone),
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    mode: "onBlur",
    defaultValues,
  });

  const invalidateUser = () =>
    queryClient.invalidateQueries({ queryKey: ["auth", "user"] });

  const saveMutation = useMutation({
    mutationFn: (values: ProfileFormValues) =>
      userApi.update(user.id, {
        firstName: values.firstName,
        lastName: values.lastName,
        phone: values.phone
          ? `${values.phoneCode} ${values.phone}`.trim()
          : "",
        timezone: values.timezone,
        preferredLanguage: values.language,
        birthDate: dmyToIso(values.birthdate),
        addressLine1: values.addressLine1 ?? "",
        city: values.city ?? "",
        state: values.state ?? "",
        postalCode: values.zip ?? "",
        country: values.country,
      }),
    onSuccess: () => {
      void invalidateUser();
      toast.success(t`Profile updated`);
      setIsEditing(false);
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to update profile`,
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteOwnAccount(),
    onSuccess: () => {
      queryClient.clear();
      toast.success(t`Your account has been deleted`);
      void navigate({ to: "/login" });
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error ? error.message : t`Failed to delete account`,
      );
    },
  });

  // Avatar persistence depends on the backend accepting `avatarUrl` (deferred);
  // the upload itself (to S3) and preview already work via FileUploader.
  const avatarMutation = useMutation({
    mutationFn: (avatarUrl: string) =>
      userApi.update(user.id, { avatarUrl }),
    onSuccess: () => void invalidateUser(),
  });

  const onSubmit = (values: ProfileFormValues) => saveMutation.mutate(values);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8">
      <TabHeader
        title={t`General Details`}
        description={t`Update your personal information and how we reach you.`}
        actions={
          isEditing ? (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  reset(defaultValues);
                  setIsEditing(false);
                }}
                disabled={saveMutation.isPending}
              >
                <Trans>Cancel</Trans>
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? t`Saving…` : t`Save`}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="brand-outline"
              onClick={() => setIsEditing(true)}
            >
              <Pencil />
              <Trans>Edit</Trans>
            </Button>
          )
        }
      />

      <fieldset
        disabled={!isEditing}
        className="flex min-w-0 flex-col gap-8 border-0 p-0"
      >
      <FormSection
        title={t`Personal details`}
        description={t`Update your contact information and preferences.`}
      >
        <div className="flex flex-col gap-5">
          <FieldGrid>
            <Field label={t`First name`} htmlFor="firstName">
              <Input
                id="firstName"
                className={controlClass}
                {...register("firstName")}
              />
              {errors.firstName ? (
                <p className="text-xs text-(--color-danger-text)">
                  {errors.firstName.message}
                </p>
              ) : null}
            </Field>
            <Field label={t`Last name`} htmlFor="lastName">
              <Input
                id="lastName"
                className={controlClass}
                {...register("lastName")}
              />
              {errors.lastName ? (
                <p className="text-xs text-(--color-danger-text)">
                  {errors.lastName.message}
                </p>
              ) : null}
            </Field>
            <Field label={t`Birthdate`} htmlFor="birthdate">
              <div className="relative">
                <Input
                  id="birthdate"
                  placeholder="DD.MM.YYYY"
                  className={cn(controlClass, "pr-10")}
                  {...register("birthdate")}
                />
                <CalendarDays className="pointer-events-none absolute right-3 top-1/2 size-5 -translate-y-1/2 text-(--color-text-secondary)" />
              </div>
            </Field>
            <Field
              label={t`Email`}
              action={
                <button
                  type="button"
                  className="text-xs font-medium text-(--color-text-secondary) transition-colors hover:text-(--color-text-primary)"
                >
                  <Trans>Change email</Trans>
                </button>
              }
            >
              <div className="flex h-11 items-center justify-between gap-2 rounded-lg border border-(--color-border) bg-(--color-surface-muted) px-3 text-sm text-(--color-text-primary)">
                <span className="truncate">{user.email}</span>
                {user.isEmailVerified !== false ? (
                  <CheckCircle2 className="size-5 shrink-0 text-(--color-success)" />
                ) : null}
              </div>
            </Field>
            <Field label={t`Phone`} htmlFor="phone">
              <div className="flex h-11 items-center overflow-hidden rounded-lg border border-(--color-border) bg-(--color-surface-content)">
                <Controller
                  control={control}
                  name="phoneCode"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger className="h-full w-auto gap-1 rounded-none border-0 bg-transparent px-3 shadow-none focus:ring-0 focus:ring-offset-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PHONE_CODES.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <div className="h-5 w-px shrink-0 bg-(--color-border)" />
                <Controller
                  control={control}
                  name="phone"
                  render={({ field }) => (
                    <input
                      id="phone"
                      type="tel"
                      placeholder="4155550192"
                      className="h-full flex-1 bg-transparent px-3 text-sm text-(--color-text-primary) outline-none placeholder:text-(--color-text-secondary)"
                      value={field.value ?? ""}
                      onChange={(event) => {
                        const normalized = event.target.value.replace(/\D/g, "");
                        field.onChange(normalized);
                      }}
                      inputMode="numeric"
                      pattern="[0-9]*"
                    />
                  )}
                />
              </div>
            </Field>
            <Field label={t`Timezone`}>
              <Controller
                control={control}
                name="timezone"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    onValueChange={(v) => field.onChange(normalizeAppTimezone(v))}
                  >
                    <SelectTrigger className={controlClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {APP_TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <Field label={t`Language`}>
              <Controller
                control={control}
                name="language"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={controlClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGE_OPTIONS.map((l) => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </FieldGrid>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-(--color-surface-muted) text-(--color-text-secondary)">
              {user.avatarUrl ? (
                <img
                  src={user.avatarUrl}
                  alt={[user.firstName, user.lastName].filter(Boolean).join(" ")}
                  className="size-16 rounded-full object-cover"
                />
              ) : (
                <span className="text-lg font-medium">{getInitials(user)}</span>
              )}
            </div>
            <FileUploader
              module="user"
              label={t`Profile photo`}
              multiple={false}
              maxFiles={1}
              maxTotalSizeMb={5}
              className="flex-1"
              onUploadSuccess={(results) => {
                const url = results[0]?.imageUrl;
                if (url) avatarMutation.mutate(url);
              }}
            />
          </div>
        </div>
      </FormSection>

      <SectionDivider />

      <FormSection
        title={t`Role & access`}
        description={t`Your role determines what you can see and do across Yes, Chef.`}
      >
        <div className="flex flex-col gap-5">
          <FieldGrid>
            <Field label={t`Role`}>
              <Input
                className={controlClass}
                value={user.roles?.[0]?.name ?? t`Member`}
                readOnly
                disabled
              />
            </Field>
            <Field label={t`Access scope`}>
              <Input
                className={controlClass}
                value={t`Org-wide`}
                readOnly
                disabled
              />
            </Field>
          </FieldGrid>
        </div>
      </FormSection>

      <SectionDivider />

      <FormSection
        title={t`Address`}
        description={t`Where you're based. Used for billing and regional defaults.`}
      >
        <div className="flex flex-col gap-5">
          <FieldGrid>
            <Field label={t`Address line 1`} htmlFor="addressLine1">
              <Input
                id="addressLine1"
                className={controlClass}
                {...register("addressLine1")}
              />
            </Field>
            <Field label={t`City`} htmlFor="city">
              <Input id="city" className={controlClass} {...register("city")} />
            </Field>
            <Field label={t`State`} htmlFor="state">
              <Input
                id="state"
                className={controlClass}
                {...register("state")}
              />
            </Field>
            <Field label={t`ZIP Code`} htmlFor="zip">
              <Controller
                control={control}
                name="zip"
                render={({ field }) => (
                  <Input
                    id="zip"
                    className={controlClass}
                    value={field.value ?? ""}
                    onChange={(event) => {
                      const normalized = event.target.value.replace(/\D/g, "").slice(0, 5);
                      field.onChange(normalized);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={5}
                  />
                )}
              />
            </Field>
            <Field label={t`Country`}>
              <Controller
                control={control}
                name="country"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={controlClass}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRY_OPTIONS.map((c) => (
                        <SelectItem key={c.value} value={c.value}>
                          {t(c.label)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </FieldGrid>
        </div>
      </FormSection>
      </fieldset>

      <SectionDivider />

      <FormSection
        title={t`Account`}
        description={t`Export your data or close your account.`}
        bare
      >
        <div className="flex flex-col gap-3">
          <SettingRow
            icon={<CloudUpload className="size-5" />}
            title={t`Download activity`}
            description={t`Export your recent audit trail.`}
            action={
              <Button
                type="button"
                variant="brand-outline"
                size="sm"
                onClick={() => setExportOpen(true)}
              >
                <Trans>Export</Trans>
              </Button>
            }
          />
          <SettingRow
            icon={<LogOut className="size-5" />}
            title={t`Sign out everywhere`}
            description={t`Revoke all sessions across devices.`}
            action={
              <Button
                type="button"
                variant="brand-outline"
                size="sm"
                onClick={() => signOut(true)}
                disabled={isSigningOut}
              >
                <Trans>Sign out</Trans>
              </Button>
            }
          />
          <SettingRow
            icon={<Trash2 className="size-5" />}
            title={t`Delete account`}
            description={t`Permanently remove your account and all data.`}
            action={
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    type="button"
                    variant="destructive-subtle"
                    size="sm"
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? t`Deleting…` : t`Delete`}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>
                      <Trans>Delete your account?</Trans>
                    </AlertDialogTitle>
                    <AlertDialogDescription>
                      <Trans>
                        This permanently removes your account and signs you out
                        on all devices. This action cannot be undone.
                      </Trans>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>
                      <Trans>Cancel</Trans>
                    </AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteMutation.mutate()}>
                      <Trans>Delete account</Trans>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            }
          />
        </div>
      </FormSection>

      <ExportActivityLogDialog open={exportOpen} onOpenChange={setExportOpen} />
    </form>
  );
}
